import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button } from '@/common/components/Button/Button';
import { ControlledDateField } from '@/common/components/Fields/ControlledDateField/ControlledDateField';
import { ControllerContext } from '@/common/components/Fields/ControllerContext/ControllerContext';
import { Form } from '@/common/components/Form/Form';
import { Icon } from '@/common/components/Icons/Icon';
import { Modal } from '@/common/components/Modal/Modal';
import { ROUTES } from '@/common/constants/routes';
import { useEmployeeDetails } from '@/common/contexts/user/UserDetailsContext';
import { useUpdateEmployeeDetails } from '@/common/integrations/menhir/employee/employee/employee.hooks';
import { useGetInsuranceAvailableOptions } from '@/common/integrations/menhir/employee/insurance/insurance.hooks';
import { formatDateISO } from '@/common/utils/date';
import { notify } from '@/common/utils/toast/toast';
import { zodResolver } from '@hookform/resolvers/zod';
import { useRouter } from 'expo-router';
import {
  TFormValues,
  birthdayDateSchema,
} from './ESHealthInsuranceAddBirthdateModal.schema';

export function ESHealthInsuranceAddBirthdateModal({
  onClose,
}: TESHealthInsuranceAddBirthdateModalProps) {
  const { t } = useTranslation();
  const { mutateAsync: updateEmployeeDetails } = useUpdateEmployeeDetails();
  const { employee } = useEmployeeDetails();
  const { push } = useRouter();
  const { refetch } = useGetInsuranceAvailableOptions({
    enabled: false,
  });
  const methods = useForm<TFormValues>({
    mode: 'onTouched',
    defaultValues: {
      birthdayDate: undefined,
    },
    resolver: zodResolver(birthdayDateSchema),
  });

  const onSubmit = async (form: TFormValues) => {
    const { birthdayDate } = form;
    try {
      await updateEmployeeDetails({
        employeeId: employee.id,
        payload: { birthdate: formatDateISO(birthdayDate) },
      });
      notify({
        message: t('general.alert.success.changes'),
        type: 'success',
      });
      const response = await refetch();
      if (response.data && response?.data?.length > 0) {
        push({
          pathname: ROUTES.INSURANCE_AVALIABLE_PLANS,
          params: { lc: 'true' },
        });
      } else {
        notify({
          message: t('dashboard.home.add_insurance.error_toast.message'),
          type: 'error',
        });
      }
    } catch (error: any) {
      if (error.statusCode === 422) {
        notify({
          message: t('dashboard.home.add_insurance.error_toast.message'),
          type: 'error',
        });
      }
    } finally {
      onClose();
    }
  };

  return (
    <FormProvider {...methods}>
      <Modal
        icon={<Icon icon="shield-plus" />}
        onClose={onClose}
        title={t('dashboard.home.add_health_insurance_modal.title')}
        footer={
          <ControllerContext
            render={({ formState: { isSubmitting } }) => (
              <Button
                type="submit"
                loading={isSubmitting}
                onPress={methods.handleSubmit(onSubmit)}
                className="grow"
              >
                {t('dashboard.home.add_health_insurance.view_plans.button')}
              </Button>
            )}
          />
        }
        overflowY="none"
      >
        <Form onSubmit={methods.handleSubmit(onSubmit)}>
          <ControlledDateField<TFormValues>
            name={'birthdayDate'}
            label={t(
              'dashboard.personal_info.sections.personal.labels.birthday_date'
            )}
          />
        </Form>
      </Modal>
    </FormProvider>
  );
}

export type TESHealthInsuranceAddBirthdateModalProps = {
  onClose: () => void;
};
